import { dialogBackground } from '@components/calendar/dialogs/planDialogSurface';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import Button from '@components/common/buttons/Button';
import ButtonWithLoader from '@components/common/buttons/ButtonWithLoader';
import ModalDialog, { DialogActions } from '@components/common/ModalDialog';
import { Row } from '@components/common/layoutUtils';
import { taskActions, taskSelectors } from '@state/task';
import { workloadActions } from '@state/workload';
import * as workloadSelectors from '@state/workload/selectors';
import { largeModalStyle, MANAGE_DIALOG_Z_INDEX } from '@utils/popupUtils';

import { categorizeCourse } from '../taskCategorization';
import { STANDALONE_SECTION_KEY, categorySectionKey, sourceSectionKey } from './constants';
import { useTaskEstimator } from './useTaskEstimator';
import { CategorySection, EstimatorBulkBar, ReadingsGroupSection, StandaloneSection } from './EstimatorSections';
import {
  CourseColor,
  EstimatorIntro,
  EstimatorRoot,
  FooterStats,
  HeadCount,
  HeadSub,
  HeadTitle,
  IntroBody,
  IntroLead,
  ProgressFill,
  ProgressSticky,
  ProgressTrack,
  TitleBar,
  TitleBarSide
} from './TaskEstimator.styles';
import { formatMinutes, isTaskEstimated } from './utils';
import { EstimatorTask } from './types';

type Props = {
  open: boolean;
  courseId: number | null;
  onClose: () => void;
};

const dialogStyle = {
  ...largeModalStyle,
  height: '90%',
  minHeight: '90%',
  paddingLeft: 24,
  paddingRight: 24,
  alignItems: 'stretch' as const
};

const TaskEstimatorDialog = ({ open, courseId, onClose }: Props) => {
  const dispatch = useDispatch();
  const theme: any = useTheme();
  const contentBackground = dialogBackground(theme);
  const model = useTaskEstimator(open, courseId);
  const {
    course,
    workloadTasks,
    state,
    dispatch: estimatorDispatch,
    estimatedCount,
    totalMinutes,
    activeTasks,
    saveDisabled,
    estimateRequest
  } = model;

  // A task opened from its row shows in its own dialog; this one steps below it until that is closed.
  const editingTask = useSelector(taskSelectors.getCurrentManagedTask).isOpen;

  // Escape closes the Estimator, but not while a task opened from it is on top: that dialog closes on Escape too, and
  // both would go. Listening for the key going down, the Estimator still sees the task's dialog open, as it closes
  // on the key coming up. An Escape that cancels something inside the Estimator, such as a rename, is left to it.
  useEffect(() => {
    if (!open || editingTask) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, editingTask, onClose]);

  const categorizingCourseIds = useSelector(workloadSelectors.getCategorizingCourseIds);
  const isCategorizingThisCourse = courseId != null && categorizingCourseIds.includes(courseId);

  const handleAutoCategorize = () => {
    if (!course || isCategorizingThisCourse) return;
    // The category picker inside the Estimator can't create new categories, so run the AI
    // categorization (which can) after closing rather than trying to patch state in place.
    // Flag a reminder so the floating status panel offers a clear way back into the Estimator;
    // cleared when they reopen from that panel, or here if the run fails outright.
    onClose();
    dispatch(workloadActions.setPendingEstimateReminder({ courseId: course.id, pending: true }));
    void categorizeCourse(dispatch, course, workloadTasks).then(ok => {
      if (!ok) {
        dispatch(workloadActions.setPendingEstimateReminder({ courseId: course.id, pending: false }));
      }
    });
  };

  const openCategories = state.categories.filter(category => {
    const tasks = model.tasksByCategory(category.id).filter(task => !task.trashed);
    return tasks.length > 0 && !tasks.every(isTaskEstimated);
  });
  // The purple outline marks the first card still waiting for an answer, in the order the cards appear.
  const isOpen = (tasks: EstimatorTask[]) => {
    const active = tasks.filter(task => !task.trashed);
    return active.length > 0 && !active.every(isTaskEstimated);
  };
  const openSource = state.sources.find(source => isOpen(model.tasksBySource(source.id)));
  const currentKey = openCategories[0]
    ? categorySectionKey(openCategories[0].id)
    : openSource
    ? sourceSectionKey(openSource.id)
    : isOpen(model.standaloneTasks)
    ? STANDALONE_SECTION_KEY
    : null;

  useEffect(() => {
    if (state.ui.scrollToTaskId == null) return;
    const row = document.querySelector(`[data-row="${state.ui.scrollToTaskId}"]`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    estimatorDispatch({ type: 'CLEAR_SCROLL' });
  }, [state.ui.scrollToTaskId, estimatorDispatch]);

  // Saves straight away, unestimated tasks or not: the Estimator can be reopened any time (Petr, 2026-10-08: a
  // "N tasks still don't have an estimate" check on every Save was too annoying).
  const submit = () => {
    if (!estimateRequest) return;
    dispatch(taskActions.estimateTasks.request(estimateRequest));
  };

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      closeOnEscape={false}
      closeOnDocumentClick={false}
      zIndex={editingTask ? MANAGE_DIALOG_Z_INDEX - 1 : undefined}
      fullWidth
      noErrorPlaceholder
      hideCloseButton
      contentStyle={dialogStyle}
      contentBackground={contentBackground}
      titleComponent={
        <TitleBar>
          <TitleBarSide>
            {course?.colorHex && <CourseColor $color={course.colorHex} />}
            <HeadSub>{course?.name || 'Course'}</HeadSub>
          </TitleBarSide>
          <HeadTitle strong>Estimate your tasks</HeadTitle>
          <TitleBarSide>
            <HeadCount>
              <b>{estimatedCount}</b> of {activeTasks.length} estimated
            </HeadCount>
          </TitleBarSide>
        </TitleBar>
      }
    >
      <EstimatorRoot>
        <ProgressSticky>
          <ProgressTrack>
            <ProgressFill $pct={activeTasks.length ? (estimatedCount / activeTasks.length) * 100 : 100} />
          </ProgressTrack>
        </ProgressSticky>
        {openCategories.length > 0 && (
          <EstimatorIntro>
            <IntroLead>Planning starts with estimating how much time you need.</IntroLead>
            <IntroBody>Pick a rough estimate for each kind of task below. You can change it anytime.</IntroBody>
          </EstimatorIntro>
        )}
        <EstimatorBulkBar model={model} />
        {state.categories.map(category => {
          const tasks = model.tasksByCategory(category.id);
          if (tasks.length === 0) return null;
          return (
            <CategorySection
              key={category.id}
              category={category}
              tasks={tasks}
              model={model}
              current={currentKey === categorySectionKey(category.id)}
            />
          );
        })}
        <ReadingsGroupSection model={model} currentKey={currentKey} />
        <StandaloneSection
          model={model}
          onAutoCategorize={handleAutoCategorize}
          isCategorizing={isCategorizingThisCourse}
          current={currentKey === STANDALONE_SECTION_KEY}
        />
      </EstimatorRoot>
      <DialogActions position="space-between" style={{ borderTop: `2px solid ${theme.borderColor}`, paddingTop: 12 }}>
        <FooterStats>
          <b>{formatMinutes(Math.round(totalMinutes)) || '0h'}</b> estimated across {estimatedCount} of {activeTasks.length}{' '}
          tasks
        </FooterStats>
        <Row style={{ gap: 10 }}>
          <Button withoutBorder onClick={onClose}>
            Cancel
          </Button>
          <ButtonWithLoader
            filled
            disabled={saveDisabled}
            onClick={submit}
            action={taskActions.estimateTasks}
            callback={onClose}
            text="Save"
          />
        </Row>
      </DialogActions>
    </ModalDialog>
  );
};

export default TaskEstimatorDialog;
